import {
  HttpClient,
  HttpContext,
  type HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { type DataResponse } from '@shared/data-response';
import { STORAGE_KEY } from '@shared/storage-key';
import { Toaster } from '@shared/toaster';
import {
  EMPTY,
  type Observable,
  catchError,
  finalize,
  map,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AppRoutes } from '../app.routes';
import type { AuthState, LoginResponse, User, UserResponse } from './auth';
import { SKIP_AUTH } from './skip-auth-token';
import { UserAdapter } from './user-adapter';
import { setUser as SentrySetUser } from '@sentry/angular';

export interface AuthTokens {
  ATO: string | null;
  RTO: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthApi {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly toaster = inject(Toaster);
  private readonly apiAuthUrl = import.meta.env.NG_APP_MDS_AUTH;
  private readonly apiUrl = import.meta.env.NG_APP_MDS_URL;

  private readonly tokens = signal<AuthTokens>({ ATO: null, RTO: null });

  private readonly authState = signal<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });

  private readonly isAuthenticated$ = computed(() => !!this.tokens().ATO);

  readonly syncEffect = effect(() => {
    const isAuth = this.isAuthenticated$();
    this.authState.update((s) => ({ ...s, isAuthenticated: isAuth }));
  });

  readonly state = this.authState.asReadonly();

  getAccessToken(): string | null {
    return this.tokens().ATO;
  }
  getRefreshToken(): string | null {
    return this.tokens().RTO;
  }
  isAuthenticated(): boolean {
    return this.isAuthenticated$();
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEY.ATO, accessToken);
    localStorage.setItem(STORAGE_KEY.RTO, refreshToken);
    this.tokens.set({ ATO: accessToken, RTO: refreshToken });
  }

  private clearTokens(): void {
    localStorage.removeItem(STORAGE_KEY.ATO);
    localStorage.removeItem(STORAGE_KEY.RTO);
    this.tokens.set({ ATO: null, RTO: null });
  }

  private setUser(user: User): void {
    localStorage.setItem(STORAGE_KEY.USER, JSON.stringify(user));
    this.authState.update((s) => ({ ...s, user }));
  }

  private getUserFromLocalStorage(): User | null {
    const user = localStorage.getItem(STORAGE_KEY.USER);
    try {
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Failed to parse user from localStorage', error);
      return null;
    }
  }

  private fetchUserProfile(): Observable<User> {
    return this.http
      .get<
        DataResponse<UserResponse>
      >(`${this.apiUrl}/boarding-system/current-user`)
      .pipe(
        map((resp) => UserAdapter(resp.data)),
        tap((user) => {
          SentrySetUser({
            username: `${user.firstName} ${user.lastName}`,
            id: user.id,
            email: user.email,
          });
        }),
      );
  }

  private handleRefreshError(err: HttpErrorResponse): void {
    if (this.shouldLogoutOnRefreshError(err)) {
      this.clearSession();
    } else {
      this.authState.update((s) => ({ ...s, isLoading: false }));
    }
  }

  private shouldLogoutOnRefreshError(err: HttpErrorResponse): boolean {
    // Transient: offline/network (0), server errors (5xx), rate limit (429)
    if (err.status === 0 || err.status >= 500 || err.status === 429)
      return false;

    // OAuth2 invalid/expired refresh token → terminal
    const oauthCode = err.error?.error ?? err.error?.code;
    if (
      err.status === 400 &&
      (oauthCode === 'invalid_grant' ||
        oauthCode === 'invalid_token' ||
        oauthCode === 'unauthorized_client')
    ) {
      return true;
    }

    // Typically terminal for refresh attempts
    if (err.status === 401 || err.status === 403) return true;

    // Other 4xx: be conservative
    return false;
  }

  initializeAuth(): void {
    const ATO = localStorage.getItem(STORAGE_KEY.ATO);
    const RTO = localStorage.getItem(STORAGE_KEY.RTO);
    const user = this.getUserFromLocalStorage();

    this.tokens.set({ ATO, RTO });

    // If we have a token, always fetch fresh user data
    if (ATO) {
      // Set cached user first for immediate UI feedback
      if (user) {
        this.authState.update((s) => ({ ...s, user }));
      }

      // Then fetch fresh user data
      this.fetchUserProfile()
        .pipe(
          tap((fetchedUser) => this.setUser(fetchedUser)),
          // If profile fails, try refresh flow
          catchError(() =>
            this.refreshToken().pipe(
              tap(({ user: refreshedUser }) => this.setUser(refreshedUser)),
              map(() => null),
            ),
          ),
          // If refresh also fails, classify and act
          catchError((refreshErr: HttpErrorResponse) => {
            this.handleRefreshError(refreshErr);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  login(phoneNumber: string, otp: string): void {
    this.authState.update((s) => ({ ...s, isLoading: true, error: null }));

    this.http
      .post<LoginResponse>(
        `${this.apiAuthUrl}/oauth/token`,
        {
          phone: phoneNumber,
          code: otp,
          grant_type: 'greeters_otp_grant',
          client_id: import.meta.env.NG_APP_CLIENT_ID,
          client_secret: import.meta.env.NG_APP_CLIENT_SECRET,
        },
        { context: new HttpContext().set(SKIP_AUTH, true) },
      )
      .pipe(
        tap((resp) => {
          this.setTokens(resp.access_token, resp.refresh_token);
          this.authState.update((s) => ({
            ...s,
            isLoading: false,
            error: null,
          }));
        }),
        switchMap(() => this.fetchUserProfile()),
        tap((user) => this.setUser(user)),
        catchError((error) => {
          this.toaster.show({
            message: error.error?.message ?? 'Login failed. Please try again.',
            type: 'error',
          });
          this.authState.update((s) => ({
            ...s,
            isLoading: false,
            error: error.error?.message ?? 'Login failed',
          }));
          return throwError(() => error);
        }),
      )
      .subscribe(() => {
        this.router.navigate([AppRoutes.SHUTTLE]);
      });
  }

  logout(): void {
    this.http
      .post<unknown>(`${this.apiAuthUrl}/api/logout`, {})
      .pipe(
        catchError(() => {
          this.toaster.show({
            message: 'Logout failed. Please check your connection.',
            type: 'error',
          });
          return EMPTY;
        }),
        finalize(() => this.clearSession()),
      )
      .subscribe();
  }

  clearSession(): void {
    localStorage.removeItem(STORAGE_KEY.ATO);
    localStorage.removeItem(STORAGE_KEY.RTO);
    localStorage.removeItem(STORAGE_KEY.USER);
    this.clearTokens();
    this.authState.update((s) => ({
      ...s,
      user: null,
      error: null,
      isLoading: false,
    }));
    this.router.navigate([AppRoutes.LOGIN]);
  }

  refreshToken(): Observable<{
    access_token: string;
    refresh_token: string;
    user: User;
  }> {
    const refreshToken =
      this.tokens().RTO ?? localStorage.getItem(STORAGE_KEY.RTO);
    if (!refreshToken) {
      this.clearSession();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<LoginResponse>(
        `${this.apiAuthUrl}/oauth/token`,
        {
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
          client_id: import.meta.env.NG_APP_CLIENT_ID,
          client_secret: import.meta.env.NG_APP_CLIENT_SECRET,
        },
        { context: new HttpContext().set(SKIP_AUTH, true) },
      )
      .pipe(
        tap((resp) => this.setTokens(resp.access_token, resp.refresh_token)),
        switchMap((resp) =>
          this.fetchUserProfile().pipe(
            tap((user) => this.setUser(user)),
            map((user) => ({
              access_token: resp.access_token,
              refresh_token: resp.refresh_token,
              user,
            })),
          ),
        ),
      );
  }

  sendOtpCode(phoneNumber: string): Observable<unknown> {
    return this.http
      .post(
        `${this.apiAuthUrl}/api/send-phone-login-code`,
        { phone: phoneNumber, provider: 'greeters' },
        { context: new HttpContext().set(SKIP_AUTH, true) },
      )
      .pipe(
        catchError((error) => {
          console.error('Error sending OTP code:', error);
          return throwError(() => error);
        }),
      );
  }
}
